import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, MoreHorizontal, Plus, Send } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Avatar } from '../../components/Avatar';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { OptionCard } from '../../components/OptionCard';
import { PageHeader } from '../../components/PageHeader';
import { InviteLinkResult } from '../../components/InviteLinkResult';
import { CreateOrInviteToggle, AccountCreatedPanel } from '../../components/CreateOrInvite';
import type { ResellerStaff as ResellerStaffType } from '../../mocks/reseller';
import { useResellerStaff, inviteResellerStaff, resendResellerStaffInvite, type ResellerInviteResult, type ResellerCreateUserResult } from '../../api/reseller';

/** Reseller staff list with invite-staff modal. */
export function Staff() {
  const qc = useQueryClient();
  const { data: resellerStaff = [] } = useResellerStaff();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [action, setAction] = useState<'create' | 'invite'>('invite');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'staff' | 'readonly'>('staff');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResellerInviteResult | ResellerCreateUserResult | null>(null);

  function closeInvite() {
    setAction('invite');
    setEmail('');
    setFullName('');
    setRole('staff');
    setPassword('');
    setError(null);
    setResult(null);
    setSubmitting(false);
    setInviteOpen(false);
  }

  async function sendInvite() {
    if (action === 'create' && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await inviteResellerStaff({
        action,
        email: email.trim(),
        fullName: fullName.trim() || undefined,
        role,
        password: action === 'create' ? password : undefined,
      });
      setResult(res);
      qc.invalidateQueries({ queryKey: ['reseller', 'staff'] });
    } catch {
      setError(
        action === 'create'
          ? 'Could not create the account. Check the details and try again.'
          : 'Could not send the invitation. Check the email and try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1200px] mx-auto">
      <PageHeader
        eyebrow="Reseller"
        title={`Staff (${resellerStaff.length})`}
        subtitle="Members of your reseller team and their access levels."
        action={
          <Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setInviteOpen(true)}>
            Invite staff
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Email</th>
                <th className="text-left font-medium px-4 py-3">Role</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Last login</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {resellerStaff.map((s) => (
                <tr key={s.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar name={s.fullName} size="md" />
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-950 dark:text-zinc-50 truncate">{s.fullName}</div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 mono truncate">{s.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 capitalize text-zinc-700 dark:text-zinc-200">{s.role}</td>
                  <td className="px-4 py-4 text-zinc-600 dark:text-zinc-300 hidden md:table-cell tabular-nums">
                    {s.lastLogin ?? '—'}
                  </td>
                  <td className="px-4 py-4">
                    <StaffStatusBadge status={s.status} />
                  </td>
                  <td className="px-5 sm:px-6 py-4 text-right">
                    {s.status === 'invited' ? (
                      s.id.startsWith('inv_') ? (
                        <ResendButton inviteId={s.id} />
                      ) : null
                    ) : (
                      <button
                        type="button"
                        className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        title="Manage"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="mt-6 text-sm text-zinc-500 dark:text-zinc-400">
        Admins can manage customers, branding, and pricing. Staff can manage customers but can't change billing config.
        Read-only members can view dashboards.
      </p>

      {/* Invite modal */}
      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title={result ? ('inviteUrl' in result ? 'Invitation sent' : 'Account created') : 'Add a staff member'}
        size="md"
        footer={
          result ? (
            <Button onClick={closeInvite}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={closeInvite}>
                Cancel
              </Button>
              <Button onClick={sendInvite} disabled={!email.trim() || submitting} loading={submitting}>
                {action === 'create' ? 'Create account' : 'Invite'}
              </Button>
            </>
          )
        }
      >
        {result ? (
          'inviteUrl' in result ? (
            <InviteLinkResult email={result.email} inviteUrl={result.inviteUrl} />
          ) : (
            <AccountCreatedPanel email={result.email} />
          )
        ) : (
          <div className="space-y-4">
            <CreateOrInviteToggle action={action} onChange={setAction} />
            <Input
              label="Email"
              required
              type="email"
              placeholder="staff@acme-resellers.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label="Full name"
              placeholder="(optional)"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            {action === 'create' && (
              <Input
                label="Password"
                required
                type="password"
                hint="Minimum 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
            <div>
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                Role <span className="text-rose-600">*</span>
              </div>
              <div className="space-y-2">
                <OptionCard selected={role === 'admin'} onSelect={() => setRole('admin')} asLabel={false}>
                  <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">Admin</div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-300">Full access to customers, branding, and billing config.</div>
                </OptionCard>
                <OptionCard selected={role === 'staff'} onSelect={() => setRole('staff')} asLabel={false}>
                  <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">Staff</div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-300">Manage customers but no billing config.</div>
                </OptionCard>
                <OptionCard selected={role === 'readonly'} onSelect={() => setRole('readonly')} asLabel={false}>
                  <div className="font-medium text-sm text-zinc-950 dark:text-zinc-50">Read-only</div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-300">View dashboards and reports only.</div>
                </OptionCard>
              </div>
            </div>
            {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}

/** Per-row "Resend" for a pending invite — re-sends the email + refreshes the 48h window. */
function ResendButton({ inviteId }: { inviteId: string }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  async function resend() {
    setState('sending');
    try {
      await resendResellerStaffInvite(inviteId);
      setState('sent');
      setTimeout(() => setState('idle'), 2200);
    } catch {
      setState('idle');
    }
  }
  return (
    <Button
      variant="secondary"
      size="sm"
      iconLeft={state === 'sent' ? <Check className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
      onClick={resend}
      loading={state === 'sending'}
      disabled={state !== 'idle'}
    >
      {state === 'sent' ? 'Sent' : 'Resend'}
    </Button>
  );
}

function StaffStatusBadge({ status }: { status: ResellerStaffType['status'] }) {
  switch (status) {
    case 'active':
      return <Badge tone="success">Active</Badge>;
    case 'invited':
      return <Badge tone="warning">Invited</Badge>;
    case 'disabled':
      return <Badge tone="error">Disabled</Badge>;
  }
}
